/**
 * Модель для работы с пользователями
 */

import { createModel } from '@rematch/core';
import { RootModel } from './index';

import UsersService, { IUserBody } from '../services/UsersService';
import NotificationsService from '../services/NotificationsService';
import ProtectionsService from '../services/ProtectionsService';
import { ICoordXY, IEvent, IId, ISelect, IUserMap, IAlert, ITrackInfo, IUserStatus } from './types';
import PositionsService from '../services/PositionsService';

export interface IData {
  protections: ISelect[];
}

interface IState {
  item: IUserMap | null;
  users: IUserMap[];
  data: IData;
  notifications: IEvent[];
  isFocus: boolean;
}

const initialState: IState = {
  item: null,
  users: [],
  data: {
    protections: []
  },
  notifications: [],
  isFocus: true
};

const findByUserId = (users: IUserMap[], userId: IId) => users.find(user => user.id === userId);

const removeStateFromTrackInfo = (trackInfo: ITrackInfo | undefined, stateId: IId): ITrackInfo | undefined => {
  if (!trackInfo) {
    return trackInfo;
  }

  return {
    ...trackInfo,
    states: (trackInfo.states || []).filter(state => state.stateId !== stateId)
  };
};

const addStateToTrackInfo = (
  trackInfo: ITrackInfo | undefined,
  status: IUserStatus
): ITrackInfo | undefined => {
  if (!trackInfo || !status.stateId) {
    return trackInfo;
  }

  if (trackInfo.states?.some(state => state.stateId === status.stateId)) {
    return trackInfo;
  }

  return {
    ...trackInfo,
    states: [...(trackInfo.states || []), status]
  };
};

const findTrackState = (trackInfo: ITrackInfo | undefined, stateId: IId) =>
  trackInfo?.states?.find(state => state.stateId === stateId);

export const infrastructureUsers = createModel<RootModel>()({
  state: initialState,
  reducers: {
    setUser(state: IState, payload: IUserMap): IState {
      return {
        ...state,
        item: payload
      };
    },
    setUsers(state: IState, payload: IUserMap[]): IState {
      return {
        ...state,
        users: payload
      };
    },
    setData(state: IState, payload: IData): IState {
      return {
        ...state,
        data: {
          ...state.data,
          ...payload
        }
      };
    },
    setNotifications(state: IState, payload: IEvent[]): IState {
      return {
        ...state,
        notifications: payload
      };
    },
    setIsFocus(state: IState, payload: boolean): IState {
      return {
        ...state,
        isFocus: payload
      };
    },
    removeUserState(state: IState, stateId: IId): IState {
      const itemHasState = Boolean(
        state.item?.trackInfo?.states?.some(status => status.stateId === stateId)
      );

      return {
        ...state,
        item:
          itemHasState && state.item
            ? {
                ...state.item,
                trackInfo: removeStateFromTrackInfo(state.item.trackInfo, stateId)
              }
            : state.item,
        users: state.users.map(user =>
          user.trackInfo?.states?.some(status => status.stateId === stateId)
            ? { ...user, trackInfo: removeStateFromTrackInfo(user.trackInfo, stateId) }
            : user
        )
      };
    },
    restoreUserState(
      state: IState,
      payload: { status: IUserStatus; userIds: IId[] }
    ): IState {
      const { status, userIds } = payload;
      const userIdSet = new Set(userIds);

      return {
        ...state,
        item:
          state.item && userIdSet.has(state.item.id)
            ? {
                ...state.item,
                trackInfo: addStateToTrackInfo(state.item.trackInfo, status)
              }
            : state.item,
        users: state.users.map(user =>
          userIdSet.has(user.id)
            ? { ...user, trackInfo: addStateToTrackInfo(user.trackInfo, status) }
            : user
        )
      };
    }
  },
  effects: (dispatch: any) => ({
      async getUser(userId: string, rootState?: any) {
        const { data } = await UsersService.getUserEmployees(userId);
        const { list } = rootState.alerts; // Получаем список алертов
        const resultData = {
          ...data,
          ...{
            position: data.trackInfo.lastPosition,
            online: data.trackInfo.lastPosition.type !== 'Offline',
            trackInfo: {
              ...data.trackInfo,
              ...{ online: data.trackInfo.lastPosition.type !== 'Offline' }
            }
          }
        };
        const trackInfoColor = Boolean(list.find((alert: IAlert) => alert.userId === resultData.userId))
          ? { trackInfo: { ...resultData.trackInfo, mapColor: 'red' } }
          : resultData.position.type === 'Offline'
          ? { trackInfo: { ...resultData.trackInfo, mapColor: 'gray' } }
          : { ...resultData.trackInfo };

        const { users } = rootState.infrastructureUsers;

        const dataUsers = users.map((user: any) => {
          const { id, position, charge } = user;

          return id === userId ? { ...resultData, ...trackInfoColor } : user;
        });

        this.setUsers(dataUsers);

        this.setUser({ ...resultData, ...trackInfoColor });
      },
      focusUser(position: ICoordXY) {
        const payload = {
          center: position,
          zoom: 20
        };
        // const payload = { center: position };

        dispatch.infrastructureMap.setFocus(payload);
      },
      resetUser() {
        this.setUser(initialState.item);
      },
      async updateUser(formBody: IUserBody) {
        const { avatar, ...body } = formBody;

        const { username } = body;

        try {
          const promises = [UsersService.updateOrCreate(body)];

          if (avatar) {
            const formData = new FormData();
            formData.append('file', avatar);
            formData.append('userName', username);

            promises.push(UsersService.updateOrCreateAvatar(formData));
          }

          await Promise.all(promises);

          this.getUser(username);
        } catch (error) {
          return error;
        }
      },
      async alertUser(payload: any, rootState?: any) {
        const { userId, ...trackInfo } = payload;
        const { users } = rootState.infrastructureUsers;
        let data = [];
        data = users.map((user: any) => (user.id === userId ? { ...user, ...trackInfo } : user));
        // console.log(data, payload);
        this.setUsers(data);
      },

      async getUsers(data: any, rootState?: any) {
        const { payload, mapId } = data;
        const { list } = rootState.alerts; // Получаем список алертов
        const { users } = rootState.infrastructureUsers; // Получаем текущий массив пользователей
        const { item } = rootState.infrastructureUsers; // Получаем карточку активного сотрудника

        // Приводим payload к массиву, если это объект
        const payloadArray = Array.isArray(payload) ? payload : [payload];
        const updatedUsers = await Promise.all(
          payloadArray.map(async userMapDataNew => {
            // const { userId, trackInfo } = userMapData;
            const userMapData = {
              userId: userMapDataNew.userId,
              trackInfo: { ...userMapDataNew },
              position: userMapDataNew.lastPosition,
              charge: userMapDataNew.charge,
              online: userMapDataNew.lastPosition.type !== 'Offline'
            };

            // Определяем цвет метки на карте
            const trackInfoColor = Boolean(list.find((alert: IAlert) => alert.userId === userMapData.userId))
              ? { trackInfo: { ...userMapData.trackInfo, mapColor: 'red' } }
              : userMapData.position.type === 'Offline'
              ? { trackInfo: { ...userMapData.trackInfo, mapColor: 'gray' } }
              : { ...userMapData.trackInfo };

            // Если карточка сотрудника активна, обновляем данные
            if (item && item.id === userMapData.userId) {
              this.setUser({ ...item, ...userMapData, ...trackInfoColor });
            }

            // Проверяем, есть ли пользователь в массиве
            const exists = findByUserId(users, userMapData.userId);

            if (exists) {
              // Если пользователь существует, обновляем его данные
              return {
                ...users.find((user: any) => user.id === userMapData.userId), // Ищем существующего пользователя
                ...userMapData,
                ...trackInfoColor
              };
            } else {
              // Если это новый пользователь, запрашиваем информацию и добавляем
              // tslint:disable-next-line:no-console
              console.log(
                'About to call getUserEmployees for userId:',
                userMapData.userId,
                'exists:',
                exists,
                'users:',
                users
              );
              const { data: user } = await UsersService.getUserEmployees(userMapData.userId);
              return {
                ...user,
                ...userMapData,
                ...trackInfoColor
              };
            }
          })
        );

        const combinedArray = [...users, ...updatedUsers];

        // Группировка пользователей по id
        const groupedUsers = combinedArray.reduce((acc: any, user: any) => {
          acc[user.id] = acc[user.id] ? [...acc[user.id], user] : [user];
          return acc;
        }, {});

        // Выбор последнего обновления для каждого пользователя
        const uniqueUsers = Object.values(groupedUsers).map(
          (userUpdates: unknown) => (userUpdates as any[])[(userUpdates as any[]).length - 1]
        );

        // Фильтрация удаленных пользователей
        const filteredUsers = uniqueUsers.filter(
          user => user.position.type !== 'Clear' || user.lastPosition.type !== 'Clear'
        );

        // Устанавливаем обновленный массив пользователей
        this.setUsers(filteredUsers);
      },

      async setUsersListInMap(payload: IId | { placeId: IId; isAllPlans: boolean }, rootState?: any) {
        const { list } = rootState.alerts; // Получаем список алертов
        const { item } = rootState.infrastructureUsers; // Получаем карточку активного сотрудника

        // Определяем параметры для API
        let apiParams: any = {};
        if (typeof payload === 'object' && payload !== null) {
          const { placeId, isAllPlans } = payload as any;
          if (isAllPlans) {
            // Загружаем всех сотрудников по офису
            apiParams = { placeId, scope: 'ALL' };
          } else {
            // Загружаем сотрудников по конкретному плану с архивными позициями
            apiParams = { mapId: placeId, scope: 'HAS_ARCHIVE_POSITION' };
          }
        } else {
          // Обратная совместимость: если передан простой ID, используем как mapId
          apiParams = { mapId: payload, scope: 'HAS_ARCHIVE_POSITION' };
        }

        const { data } = await PositionsService.getUsers(apiParams);

        const alertUserIds = new Set(list.map((alert: IAlert) => alert.userId));

        const dataMod = await data.map(
          (user: {
            user: { id: IId; username: string };
            trackInfo: {
              measurements: [{ type: string; value: string }];
              mapColor: string;
              lastPosition: Record<string, unknown>;
            };
          }) => {
            return {
              ...user.user,
              mapColor: user.trackInfo.mapColor,
              position: user.trackInfo.lastPosition,
              trackInfo: user.trackInfo
            };
          }
        );

        // Приводим payload к массиву, если это объект
        const payloadArray = Array.isArray(dataMod) ? dataMod : [dataMod];
        const updatedUsers = await Promise.all(
          payloadArray.map(async userMapData => {
            const { id: userId, trackInfo } = userMapData;

            // Определяем цвет метки на карте
            const trackInfoColor = Boolean(alertUserIds.has(userId))
              ? { trackInfo: { ...trackInfo, mapColor: 'red' } }
              : userMapData.position.type === 'Offline'
              ? { trackInfo: { ...trackInfo, mapColor: 'gray' } }
              : { ...trackInfo };

            // Если карточка сотрудника активна, обновляем данные
            if (item && item.id === userId) {
              this.setUser({ ...item, ...userMapData, ...trackInfoColor });
            }

            return {
              ...userMapData,
              ...trackInfoColor
            };
          })
        );
        return this.setUsers(updatedUsers);
      },
      resetUsers() {
        this.setUsers(initialState.users);
      },
      async getData() {
        const {
          data: { data: protections }
        } = await ProtectionsService.getTypeAll({ pageSize: 100 });

        this.setData({ protections });
      },
      async getNotifications(employeesList: IId) {
        const {
          data: { data }
        } = await NotificationsService.getFullAll({ employeesList });

        this.setNotifications(data);
      },
      focusOn() {
        this.setIsFocus(true);
      },
      focusOff() {
        this.setIsFocus(false);
      },
      async clearUserState(payload: { stateId: IId }, rootState?: any) {
        const { stateId } = payload;
        const { item, users } = rootState.infrastructureUsers;

        const removedState =
          findTrackState(item?.trackInfo, stateId) ||
          users
            .map((user: IUserMap) => findTrackState(user.trackInfo, stateId))
            .find(Boolean);

        const affectedUserIds = Array.from(
          new Set(
            [
              ...(item?.id && findTrackState(item.trackInfo, stateId) ? [item.id] : []),
              ...users
                .filter((user: IUserMap) => user.id && findTrackState(user.trackInfo, stateId))
                .map((user: IUserMap) => user.id)
            ] as IId[]
          )
        );

        if (removedState) {
          this.removeUserState(stateId);
        }

        try {
          await NotificationsService.clearState(stateId);
          this.removeUserState(stateId);
        } catch (error) {
          if (removedState) {
            this.restoreUserState({ status: removedState, userIds: affectedUserIds });
          }
          return error;
        }
      }
    })
});
